import {
  TrussInternalClient,
  type AssistantQueryRequest,
  type AssistantQueryResponse,
  type GlobalSearchRequest,
  type GlobalSearchResponse,
  type VectorSearchRequest,
  type VectorSearchResponse,
  type SimilarSearchOptions,
  type SimilarSearchResponse,
  type SearchProductsRequest,
  type SearchProductsResponse,
  type SmartSearchRequest,
  type SmartSearchResponse,
  type KnowledgeSearchRequest,
  type KnowledgeSearchResponse,
} from '@truss-security/truss-sdk-internal';
import { supabase } from '@/lib/supabaseClient';
import { trussClientBase, trussSearchClientBase } from '@/shared/constants';

/**
 * SearchService - Wrapper around {@link TrussInternalClient} for search functionality
 * 
 * This service provides a consistent interface for search operations and handles:
 * - SDK initialization and configuration
 * - Error handling and logging
 * - Data transformation and validation
 * - Caching and retry logic (future)
 */
export class SearchService {
  private client: TrussInternalClient;
  private isInitialized = false;

  constructor() {
    this.initializeClient();
  }

  /**
   * Initialize the Truss client with configuration
   */
  private initializeClient(): void {
    try {
      const baseUrl = trussSearchClientBase || trussClientBase;
      if (!baseUrl) throw new Error('Truss search base missing');

      this.client = new TrussInternalClient({
        baseUrl,
        timeout: 30000,
        retries: 3,
      });

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize SearchService:', error);
      throw new Error('SearchService initialization failed');
    }
  }

  /**
   * Ensure the service is initialized before making requests
   */
  private ensureInitialized(): void {
    if (!this.isInitialized) {
      throw new Error('SearchService is not initialized');
    }
  }

  /**
   * Perform a global search across all products
   * 
   * @param params - Search parameters
   * @returns Promise resolving to search results
   */
  // async globalSearch(params: GlobalSearchRequest): Promise<GlobalSearchResponse> {
  //   this.ensureInitialized();
    
  //   try {
  //     const results = await this.client.search.global(params);
      
  //     // Add any post-processing here
  //     return results;
  //   } catch (error) {
  //     console.error('Global search failed:', error);
  //     this.handleError(error, 'globalSearch');
  //     throw error;
  //   }
  // }

  /**
   * Perform a vector similarity search using AI embeddings
   * 
   * @param params - Vector search parameters
   * @returns Promise resolving to vector search results
   */
  // async vectorSearch(params: VectorSearchRequest): Promise<VectorSearchResponse> {
  //   this.ensureInitialized();
    
  //   try {
  //     const results = await this.client.search.vector(params);
      
  //     return results;
  //   } catch (error) {
  //     console.error('Vector search failed:', error);
  //     this.handleError(error, 'vectorSearch');
  //     throw error;
  //   }
  // }

  /**
   * Find products similar to a specific product
   * 
   * @param productId - The ID of the product to find similar products for
   * @param params - Similar products search parameters
   * @returns Promise resolving to similar products
   */
  // async findSimilarProducts(
  //   productId: number, 
  //   params: SimilarSearchOptions = {}
  // ): Promise<SimilarSearchResponse> {
  //   this.ensureInitialized();
    
  //   try {
  //     const results = await this.client.search.similar(productId, params);
      
  //     return results;
  //   } catch (error) {
  //     console.error('Similar products search failed:', error);
  //     this.handleError(error, 'findSimilarProducts');
  //     throw error;
  //   }
  // }

  /**
   * POST /product/search — filter via `filterExpression` (FilterQL string), not `filterAst`.
   *
   * @param params - Product search parameters (`filterExpression`, dates, pagination, sort)
   * @returns Promise resolving to product search results
   */
  async searchProducts(params: SearchProductsRequest): Promise<SearchProductsResponse> {
    this.ensureInitialized();
    
    try {
      const results = await this.client.search.products(params);
      
      return results;
    } catch (error) {
      console.error('Product search failed:', error);
      this.handleError(error, 'searchProducts');
      throw error;
    }
  }

  /**
   * Perform an intelligent smart search that uses AI to parse natural language queries
   * into structured filters and combines traditional and vector search for optimal results
   * 
   * @param params - Smart search parameters including natural language query
   * @returns Promise resolving to smart search results with parsed filters and optional AI response
   */
  async smartSearch(params: SmartSearchRequest): Promise<SmartSearchResponse> {
    this.ensureInitialized();
    
    try {
      const results = await this.client.search.smart(params);
      return results;
    } catch (error) {
      console.error('Smart search failed:', error);
      this.handleError(error, 'smartSearch');
      throw error;
    }
  }

  /**
   * Internal doc RAG (truss-intelligence). Requires test/prod API deploy + SEARCH_SERVER_URL + ingest.
   */
  async knowledgeSearch(params: KnowledgeSearchRequest): Promise<KnowledgeSearchResponse> {
    this.ensureInitialized();

    try {
      return await this.client.search.knowledge(params);
    } catch (error) {
      console.error('Knowledge search failed:', error);
      this.handleError(error, 'knowledgeSearch');
      throw error;
    }
  }

  /**
   * Assistant orchestration (server-routed smart + knowledge search).
   */
  async assistantQuery(
    params: AssistantQueryRequest & {
      messages?: Array<{ role: 'user' | 'assistant'; content: string }>;
    }
  ): Promise<AssistantQueryResponse> {
    this.ensureInitialized();

    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      const response = await fetch(`${trussSearchClientBase || trussClientBase}/assistant/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(params),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Assistant query failed (${response.status})`);
      }
      return await response.json() as AssistantQueryResponse;
    } catch (error) {
      console.error('Assistant query failed:', error);
      this.handleError(error, 'assistantQuery');
      throw error;
    }
  }

  /**
   * Get the current client configuration
   * 
   * @returns Current configuration
   */
  getConfig() {
    this.ensureInitialized();
    return this.client.getConfig();
  }

  /**
   * Centralized error handling
   * 
   * @param error - The error that occurred
   * @param method - The method where the error occurred
   */
  private handleError(error: any, method: string): void {
    // Log error details
    console.error(`SearchService.${method} error:`, {
      message: error.message,
      status: error.status || error.response?.status,
      data: error.response?.data,
      method,
      timestamp: new Date().toISOString(),
    });

    // You can add additional error handling here:
    // - Send to error tracking service
    // - Show user-friendly error messages
    // - Implement retry logic
    // - Cache fallback responses
  }

  /**
   * Check if the service is properly initialized
   * 
   * @returns True if initialized, false otherwise
   */
  isReady(): boolean {
    return this.isInitialized;
  }
}

// Export a singleton instance
export const searchService = new SearchService();

